<template>
  <div>
    <v-card-title class="pa-1 mt-1">
      <h4 :class="fieldError ? 'red--text wiggle' : 'white--text'" v-html="field.label"></h4>
    </v-card-title>

    <v-container v-if="loading" fill-height>
      <v-progress-circular :width="3" :size="25" indeterminate color="blue" />
    </v-container>

    <span v-if="!field.multivalued && !loading">
      <v-chip v-if="value!=null" class="mr-2">
        <strong class="mx-2 overflow-x-hidden"> <span>{{ label(newValue) }}</span> </strong>
        <v-icon v-if="store.getters.permissionToWrite" small @click="newValue = null; changed = true">delete</v-icon>
      </v-chip>
    </span>

    <draggable v-else-if="!loading" v-model="newValue" :disabled="!store.getters.permissionToWrite">
      <div
        v-for="(item, index) in newValue"
        :key="index"
        class="pa-1 mt-1"
      >
        <v-chip class="mr-2">
          <strong class="mx-2 overflow-x-hidden" :class="{ 'move-cursor': newValue.length > 1 }"> <span>{{ label(item) }}</span> </strong>
          <v-icon v-if="store.getters.permissionToWrite" small @click="remove(index)">delete</v-icon>
        </v-chip>
      </div>
    </draggable>

    <v-btn v-if="!loading && store.getters.permissionToWrite" icon @click="field.dialog = true; changed = true">
      <v-icon :color="fieldError ? 'red' : ''">add_to_photos</v-icon>
    </v-btn>

    <v-card-subtitle v-if="fieldError" class="px-0 py-0">
      <span class="red--text">{{ fieldError }}</span>
    </v-card-subtitle>

    <v-text-field v-show="false" ref="pickerRules" v-model="newValue" :rules="field.rules" readonly />
    <PickerExternalPopup
      v-show="field.dialog"
      :opened="field.dialog"
      :field="field"
      :handle-pick="handlePick"
      :close="closePickerPopup"
      :filters="filters"
    />
  </div>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vue2-helpers/vuex';
import draggable from 'vuedraggable';
import { showInListFields } from '@/utils/content';

import PickerExternalPopup from './PickerExternal/PickerExternalPopup.vue';

const store = useStore();

const emit = defineEmits(['input', 'updateNewContent']);

const props = defineProps({
  field: { type: Object, default () { return {}; } },
  value: [Object, Array, String],
  content: { type: Object, default () { return {}; } },
  filters: { type: Array, default: () => [] }
});

const pickerRules = ref(null);
const newValue = ref(props.value);
const rawContentTypes = ref(store.state.rawContentTypes);
const loading = ref(false);
const isMounted = ref(false);
const changed = ref(false);

const fieldError = computed(() => {
  if (!isMounted.value || !changed.value) return;
  const rules = pickerRules.value;

  if (rules.valid) {
    return;
  }

  return (rules && rules.errorBucket.length) ? rules.errorBucket[0] : null;
});

const generateLabel = (inListFields, value) => {
  const items = [];

  for (const [key, field] of Object.entries(inListFields)) {
    // We only add items that we have (saved fields)
    if (!value[key]) {
      continue;
    }

    if (field.isTitle) {
      items.push(value[key]);
    }
  }

  return items ? items.join(' | ') : null;
};

const label = item => {
  const inListFields = showInListFields(rawContentTypes.value[props.field.type].fields);

  return generateLabel(inListFields, item);
};

const closePickerPopup = () => {
  props.field.dialog = false;
};

const remove = index => {
  // remove the item
  changed.value = true;
  newValue.value.splice(index, 1);
};

const handlePick = item => {
  if (!props.field.multivalued) {
    newValue.value = item;
  } else {
    if (!newValue.value) {
      newValue.value = [];
    }

    let addItem = true;
    newValue.value.forEach(content => {
      // Check if we already have the object in the list
      if (_.isEqual(content, item)) {
        addItem = false;
      }
    });

    if (addItem) {
      newValue.value.push(item);
    }
  }

  props.field.dialog = false;
};

onMounted(() => {
  isMounted.value = true;
});

watch(newValue, val => {
  emit('input', val);
  if (val) {
    if (props.field.fieldToSync && props.field.fieldToSync.length) {
      const { content } = props;
      props.field.fieldToSync.map(field => {
        if (val[field.updateField]) {
          content[field.retrieveField] = val[field.updateField];
        }
      });
      emit('updateNewContent', content);
    }
  } else {
    const { content } = props;
    props.field.fieldToSync.map(field => {
      content[field.retrieveField] = null;
    });
    emit('updateNewContent', content);
  }
});
</script>

<style scoped>
.loading{
    margin: 12px;
}
</style>
